import React, { useState, useEffect, useCallback } from 'react';
import Breadcrumbs from '../../components/shared/breadcrumbs/Breadcrumbs';
import Tabs from '../../components/shared/tabs/Tabs';
import ProcessingTable from '../../components/pages/process/ProcessingTable/ProcessingTable';
import OnlineFormFillerTable from '../../components/pages/process/OnlineFormFillerTable/OnlineFormFillerTable';
import UploadedDocumentsTable from '../../components/pages/process/UploadedDocumentsTable/UploadedDocumentsTable';
import Chatbot from '../../components/shared/chatbot/Chatbot';
import './Process.scss';
import {
    useDeleteV1ProcessDeleteProcessProcessId,
    useGetV1IdentityUserInfo,
    useGetV1ProcessGetDocumentsProcessingInProcessProcessId,
    useGetV1ProcessGetUploadedDocumentsInProcessProcessId,
    useGetV1ProcessGetOnlineFormsInProcessProcessId,
    useGetV1ProcessProcessIdGetProcessDetails,
} from '../../api/generated/endpoint';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button, Input } from '@appkit4/react-components';
import Roles from '../../auth/roles';
import { DeleteModal } from '../../components/shared/deleteModal';
import Illustration6 from '../../assets/illustrations/Illustration_6.png';
import useToast from '../../helpers/useToast';
import useLocale from '../../locale/useLocale';
import UploadModal from '../../components/shared/uploadModal/UploadModal';

enum ProcessType {
    PDFExtraction = 1,
    FormFiller = 2,
    OnlineFormFiller = 3,
}

const getProcessTypeName = (processTypeId: number): string => {
    switch (processTypeId) {
        case ProcessType.PDFExtraction:
            return 'PDF Extraction';
        case ProcessType.FormFiller:
            return 'Form Filler';
        case ProcessType.OnlineFormFiller:
            return 'Online Form Filler';
        default:
            return 'Unknown Process Type';
    }
};

const ProcessPage = () => {
    const { processId } = useParams<{ processId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const {
        uploadedListingTab,
        processingListingTab,
        onlineFormListingTab,
        showUploadModal,
        validationErrors,
        validationWarnings,
        originalFile,
    } = location.state || {};
    const [currentPageUploaded, setCurrentPageUploaded] = useState(1);
    const [showUploadModalState, setShowUploadModalState] = useState(
        showUploadModal || false
    );
    const [preloadedValidationErrors] = useState(validationErrors || []);
    const [preloadedValidationWarnings] = useState(validationWarnings || []);
    const [preloadedOriginalFile] = useState<File | null>(originalFile || null);
    const [currentPageProcessing, setCurrentPageProcessing] = useState(1);
    const [currentPageOnlineForms, setCurrentPageOnlineForms] = useState(1);
    const pageSize = 10;
    const [activeDocumentTab, setActiveDocumentTab] = useState(0);
    const [selectedDocTypes, setSelectedDocTypes] = useState(
        location.state?.selectedDocTypes || []
    );
    const [uploadedDocumentsTab, setUploadedDocumentsTab] = useState(
        typeof uploadedListingTab === 'number' ? uploadedListingTab : 0
    );
    const [processingDocumentsTab, setProcessingDocumentsTab] = useState(
        typeof processingListingTab === 'number' ? processingListingTab : 0
    );
    const [onlineFormsTab, setOnlineFormsTab] = useState(
        typeof onlineFormListingTab === 'number' ? onlineFormListingTab : 0
    );
    const [allUploadedDocuments, setAllUploadedDocuments] = useState<any[]>([]);
    const [shouldFetch, setShouldFetch] = useState(false);
    const [allProcessingDocuments, setAllProcessingDocuments] = useState<any[]>(
        []
    );
    const [allOnlineForms, setAllOnlineForms] = useState<any[]>([]);
    const [refreshUploadedDocuments, setRefreshUploadedDocuments] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const handleCloseUploadModal = () => {
        setShowUploadModalState(false);
    };

    const { data: processDetails } = useGetV1ProcessProcessIdGetProcessDetails(
        Number(processId)
    );

    const { data: userInfo } = useGetV1IdentityUserInfo({
        query: {
            staleTime: Infinity,
        },
    });
    useEffect(() => {
        const timer = setTimeout(() => {
            setShouldFetch(true);
        }, 3000);

        return () => clearTimeout(timer);
    }, []);

    const breadcrumbItems = React.useMemo(() => {
        const items = [{ label: 'My Engagements', href: '/folder' }];

        if (processDetails?.folderStructure) {
            const addFolderToBreadcrumb = (folder: any) => {
                if (folder) {
                    items.push({
                        label: folder.folderName,
                        href: `/folder/${folder.folderId}`,
                    });
                    if (folder.childFolder) {
                        addFolderToBreadcrumb(folder.childFolder);
                    }
                }
            };

            addFolderToBreadcrumb(processDetails.folderStructure);
        }

        // Add the current process as the last item
        items.push({
            label: processDetails?.processName || 'Process',
            href: `/process/${processId}`,
        });

        return items;
    }, [processDetails, processId]);

    const handleBreadcrumbClick = (index: number) => {
        const item = breadcrumbItems[index];
        if (item.href) {
            navigate(item.href);
        }
    };

    const documentTabs = [
        {
            label: 'Classification',
            value: 'uploaded',
            icon: 'icon-upload-light-outline',
        },
        {
            label: 'Validation and Extraction',
            value: 'processing',
            icon: 'icon-gantt-chart-outline',
        },
    ];

    const {
        data: allUploadedDocumentsData,
        isLoading: isLoadingAllUploaded,
        refetch: refetchUploadedDocuments,
    } = useGetV1ProcessGetUploadedDocumentsInProcessProcessId(
        Number(processId),
        { skip: 0, take: 1000 },
        {
            query: {
                enabled: shouldFetch && processDetails?.processTypeId === 1,
            },
        }
    );

    const {
        data: allProcessingDocumentsData,
        isLoading: isLoadingAllProcessing,
        refetch: refetchProcessingDocuments,
    } = useGetV1ProcessGetDocumentsProcessingInProcessProcessId(
        Number(processId),
        { skip: 0, take: 1000 },
        {
            query: {
                enabled: shouldFetch && processDetails?.processTypeId === 1,
            },
        }
    );

    const {
        data: allOnlineFormsData,
        isLoading: isLoadingAllOnlineForms,
        refetch: refetchOnlineForms,
    } = useGetV1ProcessGetOnlineFormsInProcessProcessId(
        Number(processId),
        { skip: 0, take: 1000 },
        {
            query: {
                enabled:
                    shouldFetch &&
                    (processDetails?.processTypeId === 2 ||
                        processDetails?.processTypeId === 3),
            },
        }
    );

    useEffect(() => {
        if (allUploadedDocumentsData?.uploadedDocuments) {
            setAllUploadedDocuments(allUploadedDocumentsData.uploadedDocuments);
        }
    }, [allUploadedDocumentsData, refreshUploadedDocuments]);

    useEffect(() => {
        if (allProcessingDocumentsData?.processingDocuments) {
            setAllProcessingDocuments(
                allProcessingDocumentsData.processingDocuments
            );
        }
    }, [allProcessingDocumentsData]);

    useEffect(() => {
        if (allOnlineFormsData?.onlineForms) {
            setAllOnlineForms(allOnlineFormsData.onlineForms);
        }
    }, [allOnlineFormsData]);

    const filteredUploadedDocuments = React.useMemo(() => {
        if (uploadedDocumentsTab === 0) {
            return allUploadedDocuments;
        } else if (uploadedDocumentsTab === 1) {
            return allUploadedDocuments.filter(
                (doc) => doc.uploadedDocumentStatus === 5
            );
        } else if (uploadedDocumentsTab === 2) {
            return allUploadedDocuments.filter(
                (doc) => doc.uploadedDocumentStatus === 6
            );
        }
        return allUploadedDocuments;
    }, [allUploadedDocuments, uploadedDocumentsTab, refreshUploadedDocuments]);

    const filteredProcessingDocuments = React.useMemo(() => {
        if (processingDocumentsTab === 0) {
            return allProcessingDocuments;
        } else if (processingDocumentsTab === 1) {
            return allProcessingDocuments.filter(
                (doc) => doc.processStatus !== 8
            );
        } else if (processingDocumentsTab === 2) {
            return allProcessingDocuments.filter(
                (doc) =>
                    doc.processStatus === 8 ||
                    doc.processStatus === 10 ||
                    doc.processStatus === 12
            );
        }
        return allProcessingDocuments;
    }, [allProcessingDocuments, processingDocumentsTab]);

    const filteredOnlineForms = React.useMemo(() => {
        // No filtering here - let OnlineFormFillerTable handle all tab filtering
        return allOnlineForms;
    }, [allOnlineForms]);

    const paginatedUploadedDocuments = React.useMemo(() => {
        return filteredUploadedDocuments;
    }, [
        filteredUploadedDocuments,
        currentPageUploaded,
        pageSize,
        refreshUploadedDocuments,
    ]);

    const paginatedProcessingDocuments = React.useMemo(() => {
        return filteredProcessingDocuments;
    }, [filteredProcessingDocuments, currentPageProcessing, pageSize]);

    const paginatedOnlineForms = React.useMemo(() => {
        return filteredOnlineForms;
    }, [filteredOnlineForms, currentPageOnlineForms, pageSize]);

    // Handle main document tab changes
    useEffect(() => {
        if (location.pathname.endsWith('/uploaded')) {
            setActiveDocumentTab(0);
        } else if (location.pathname.endsWith('/document')) {
            setActiveDocumentTab(1);
        } else {
            navigate(`/process/${processId}/uploaded`, {
                state: {
                    uploadedListingTab: uploadedDocumentsTab,
                    onlineFormListingTab: onlineFormListingTab,
                },
                replace: true,
            });
        }
    }, [
        location.pathname,
        processId,
        navigate,
        uploadedDocumentsTab,
        onlineFormListingTab,
    ]);

    useEffect(() => {
        if (
            typeof uploadedListingTab === 'number' &&
            location.pathname.endsWith('/uploaded')
        ) {
            setUploadedDocumentsTab(uploadedListingTab);
        }
        if (
            typeof processingListingTab === 'number' &&
            location.pathname.endsWith('/document')
        ) {
            setProcessingDocumentsTab(processingListingTab);
            if (location.state?.selectedDocTypes) {
                setSelectedDocTypes(location.state.selectedDocTypes);
            }
        }
        if (
            typeof onlineFormListingTab === 'number' &&
            location.pathname.endsWith('/uploaded')
        ) {
            setOnlineFormsTab(onlineFormListingTab);
        }
    }, [uploadedListingTab, processingListingTab, location.pathname]);

    const handleDocumentTabChange = (index: number) => {
        setActiveDocumentTab(index);
        if (index === 0) {
            navigate(`/process/${processId}/uploaded`, {
                state: { uploadedListingTab: uploadedDocumentsTab },
                replace: true,
            });
        } else {
            navigate(`/process/${processId}/document`, {
                state: { processingListingTab: processingDocumentsTab },
                replace: true,
            });
        }
    };

    const handleUploadedDocumentsTabChange = (index: number) => {
        setUploadedDocumentsTab(index);
        setCurrentPageUploaded(1);
    };

    const handleProcessingDocumentsTabChange = (index: number) => {
        setProcessingDocumentsTab(index);
        setCurrentPageProcessing(1);
    };

    const handleOnlineFormsTabChange = (index: number) => {
        setOnlineFormsTab(index);
        setCurrentPageOnlineForms(1);
    };

    const handleUploadComplete = useCallback(() => {
        setShowUploadModalState(false);
        setIsRefreshing(true);

        setTimeout(() => {
            refetchUploadedDocuments()
                .then(() => {
                    setRefreshUploadedDocuments((prev) => prev + 1);
                    setCurrentPageUploaded(1);
                    setIsRefreshing(false);
                })
                .catch(() => {
                    setIsRefreshing(false);
                });
            refetchOnlineForms();
        }, 3000);
    }, [refetchUploadedDocuments, refetchOnlineForms]);

    const handleDelete = () => {
        setConfirmationVisible(true);
    };
    const [inputValue, setInputValue] = useState('');
    const [confirmationVisible, setConfirmationVisible] = useState(false);
    const { mutate: deleteProcess } =
        useDeleteV1ProcessDeleteProcessProcessId();
    const showToast = useToast();
    const { l } = useLocale();

    const confirmDeletion = () => {
        deleteProcess(
            { processId: Number(processId) },
            {
                onSuccess: () => {
                    showToast(
                        l('_success'),
                        'Process has been deleted',
                        'success'
                    );
                    setConfirmationVisible(false);
                },
            }
        );
    };

    const deleteModalContent = () => (
        <div className="del-modal-content-wrapper">
            <img
                src={Illustration6}
                alt="Delete Confirmation"
                className="del-modal-image"
            />
            <p>Are you sure you want to delete this process?</p>
            <Input
                type="text"
                placeholder="Delete"
                className="del-folder-confirmation-input"
                value={inputValue}
                onChange={(value: string) => setInputValue(value)}
            />
            <p style={{ fontSize: 14 }}>Type “Delete” To Delete This Process</p>
            <Button
                onClick={confirmDeletion}
                kind="danger"
                disabled={inputValue.toLowerCase() !== 'delete'}
                style={{ marginTop: '40px' }}
            >
                Delete
            </Button>
        </div>
    );
    const handleProcessingRefresh = useCallback(() => {
        setIsRefreshing(true);
        refetchProcessingDocuments()
            .then(() => {
                setShouldFetch(true);
            })
            .finally(() => {
                setIsRefreshing(false);
            });
    }, [refetchProcessingDocuments]);

    const handleOnlineFormsRefresh = useCallback(() => {
        setIsRefreshing(true);
        refetchOnlineForms()
            .then(() => {
                setShouldFetch(true);
            })
            .finally(() => {
                setIsRefreshing(false);
            });
    }, [refetchOnlineForms]);
    const handleRefresh = useCallback(() => {
        setIsRefreshing(true);
        Promise.all([
            refetchUploadedDocuments(),
            refetchProcessingDocuments(),
            refetchOnlineForms(),
        ]).finally(() => {
            setIsRefreshing(false);
            setShouldFetch(true);
            setRefreshUploadedDocuments((prev) => prev + 1);
        });
    }, [
        refetchUploadedDocuments,
        refetchProcessingDocuments,
        refetchOnlineForms,
    ]);

    return (
        <div className="process-page">
            <div className="breadcrumb-container">
                <div className="breadcrumb-list">
                    <Breadcrumbs
                        items={breadcrumbItems}
                        onClick={handleBreadcrumbClick}
                    />
                </div>
                <div className="breadcrumb-actions">
                    <Button
                        kind="tertiary"
                        icon="icon-refresh-outline"
                        onClick={handleRefresh}
                        loading={isRefreshing}
                        style={{ marginRight: '8px' }}
                    >
                        Refresh
                    </Button>
                    {(userInfo?.roleName === 'Admin' ||
                        userInfo?.roleId === Roles.SuperAdminId) && (
                        <Button
                            kind="tertiary"
                            icon="icon-archive-outline"
                            onClick={handleDelete}
                        >
                            Delete Process
                        </Button>
                    )}
                </div>
            </div>

            {processDetails && (
                <div className="ap-typography-heading-l">
                    <h1>{processDetails?.processName || 'Process'}</h1>
                    <h4>
                        Process Type:{' '}
                        {getProcessTypeName(
                            Number(processDetails?.processTypeId)
                        )}
                    </h4>
                    <h6>
                        Document Types:{' '}
                        {processDetails?.documentTypeNames?.join(', ')}
                    </h6>
                </div>
            )}

            <DeleteModal
                content={deleteModalContent()}
                visible={confirmationVisible}
                onClose={() => setConfirmationVisible(false)}
                title="Delete Process"
            />

            {processDetails?.processTypeId === 2 ||
            processDetails?.processTypeId === 3 ? (
                <OnlineFormFillerTable
                    filteredData={paginatedOnlineForms}
                    currentPage={currentPageOnlineForms}
                    pageSize={pageSize}
                    setCurrentPage={setCurrentPageOnlineForms}
                    totalCount={filteredOnlineForms.length}
                    isLoading={isLoadingAllOnlineForms || isRefreshing}
                    activeTab={onlineFormsTab}
                    onTabChange={handleOnlineFormsTabChange}
                    processName={processDetails?.processName ?? 'Process'}
                    onDocumentUpdate={handleOnlineFormsRefresh}
                    processTypeId={processDetails?.processTypeId}
                    onUploadComplete={handleUploadComplete}
                    processDocumentTypeNames={
                        processDetails?.documentTypeNames ?? []
                    }
                />
            ) : (
                <>
                    <div className="document-tabs">
                        <Tabs
                            tabs={documentTabs}
                            activeTab={activeDocumentTab}
                            onTabChange={handleDocumentTabChange}
                        />
                    </div>

                    {activeDocumentTab === 0 ? (
                        <UploadedDocumentsTable
                            filteredData={paginatedUploadedDocuments}
                            currentPage={currentPageUploaded}
                            pageSize={pageSize}
                            setCurrentPage={setCurrentPageUploaded}
                            totalCount={filteredUploadedDocuments.length}
                            isLoading={isLoadingAllUploaded || isRefreshing}
                            activeTab={uploadedDocumentsTab}
                            onTabChange={handleUploadedDocumentsTabChange}
                            processName={
                                processDetails?.processName ?? 'Process'
                            }
                            onUploadComplete={handleUploadComplete}
                        />
                    ) : (
                        <ProcessingTable
                            filteredData={paginatedProcessingDocuments}
                            currentPage={currentPageProcessing}
                            pageSize={pageSize}
                            setCurrentPage={setCurrentPageProcessing}
                            totalCount={filteredProcessingDocuments.length}
                            isLoading={isLoadingAllProcessing || isRefreshing}
                            activeTab={processingDocumentsTab}
                            filterDocTypes={selectedDocTypes}
                            onTabChange={handleProcessingDocumentsTabChange}
                            processName={
                                processDetails?.processName ?? 'Process'
                            }
                            onDocumentUpdate={handleProcessingRefresh}
                            processTypeId={processDetails?.processTypeId}
                        />
                    )}
                </>
            )}

            <Chatbot />
            <UploadModal
                visible={showUploadModalState}
                onClose={handleCloseUploadModal}
                onUpload={handleUploadComplete}
                processId={processId}
                processTypeId={processDetails?.processTypeId}
                preloadedValidationErrors={preloadedValidationErrors}
                preloadedValidationWarnings={preloadedValidationWarnings}
                preloadedOriginalFile={preloadedOriginalFile}
            />
        </div>
    );
};

export default ProcessPage;
